import clsx from 'clsx';
import { JSX } from 'solid-js';
import { ErrorCatcher } from '../../../tools/error-catcher';

type RadioProps = {
    whenClicked?: (e?: any) => void;
    value: string;
    isChecked: boolean;
    name: string;
    id?: string;
    readableName?: string | JSX.Element;
    title?: string;
    hasErrors?: boolean;
    disabled?: boolean;
    'aria-label'?: string;
    'aria-describedby'?: string;
};

/**
 * The native `<input>` is `appearance-none`; the inner dot is a sibling span shown via the
 * `peer` / `peer-checked` cascade.
 */
export const Radio = (props: RadioProps) => {
    const inputId = () => `radio-${(props.id || props.value || '')}`;

    return (
        <ErrorCatcher componentName='Radio'>
            <label
                for={inputId()}
                class={clsx(
                    'inline-flex items-center gap-cvi-5 py-cvi-4',
                    props.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                )}
            >
                <span class="relative inline-flex shrink-0 size-cvi-8">
                    <input
                        id={inputId()}
                        onClick={props.whenClicked}
                        type='radio'
                        checked={props.isChecked}
                        value={props.value}
                        name={props.name}
                        disabled={props.disabled}
                        title={props.title}
                        aria-label={props['aria-label']}
                        aria-describedby={props['aria-describedby']}
                        class={clsx(
                            'peer absolute inset-0 m-0 appearance-none cursor-pointer rounded-cvi-round border bg-cvi-bg-field',
                            'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-offset-cvi-bg-body',
                            props.hasErrors
                                ? 'border-cvi-border-danger checked:bg-cvi-bg-danger focus-visible:ring-cvi-focus-danger'
                                : 'border-cvi-border-strong checked:bg-cvi-bg-interactive checked:border-cvi-border-interactive focus-visible:ring-cvi-focus-default focus-visible:border-cvi-border-interactive',
                        )}
                    />
                    {/* Inner dot — shown only when peer (`<input>`) is checked. */}
                    <span class="absolute inset-0 m-auto size-cvi-4 rounded-cvi-round bg-cvi-bg-field hidden peer-checked:block pointer-events-none" />
                </span>
                {props.readableName && (
                    <span class="label-md font-cvi-regular text-cvi-fg-default">
                        {props.readableName}
                    </span>
                )}
            </label>
        </ErrorCatcher>
    );
};
